/**
 * RunElement - Base class for all RunsNative web components
 * Ported from YenSid v3 yensid-element-contract-v1.4
 *
 * Provides:
 * - Declarative property system with type coercion
 * - Attribute reflection (bidirectional)
 * - Lifecycle hooks (willUpdate, updated, firstUpdated)
 * - Batched async updates
 * - Shadow DOM setup
 * - Event helpers (emit, listen)
 * - Reduced motion preference detection (v1.3)
 */

// Static MediaQueryList shared across all instances
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

// Container query support detection (cached)
const cqSupported = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('container-type', 'inline-size');

// Counter for unique view-transition-name generation
let viewTransitionCounter = 0;

// Track which classes have had properties set up
const initializedClasses = new WeakSet();

/**
 * Simple html tagged template literal for readability.
 * Returns a string with interpolated values.
 * @param {TemplateStringsArray} strings - Template literal strings
 * @param {...any} values - Interpolated values
 * @returns {string} Rendered HTML string
 */
export function html(strings, ...values) {
  return strings.reduce((result, str, i) => {
    const value = values[i] !== undefined ? values[i] : '';
    return result + str + value;
  }, '');
}

/**
 * Convert property name to attribute name (camelCase to kebab-case)
 */
function toAttributeName(propName, config) {
  if (config.attribute) return config.attribute;
  return propName.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * Setup property accessors for a class (called once per class)
 */
function setupProperties(ElementClass) {
  if (initializedClasses.has(ElementClass)) return;

  const props = ElementClass.properties || {};
  const attrToProperty = new Map();

  for (const [name, config] of Object.entries(props)) {
    // Skip if property already defined (e.g., reducedMotion)
    const existing = Object.getOwnPropertyDescriptor(ElementClass.prototype, name);
    if (existing && (existing.get || existing.set)) continue;

    const attrName = toAttributeName(name, config);
    attrToProperty.set(attrName, { name, config });

    Object.defineProperty(ElementClass.prototype, name, {
      get() {
        return this._properties[name];
      },
      set(value) {
        const oldValue = this._properties[name];

        // Type coercion
        if (config.type === Boolean) {
          value = Boolean(value);
        } else if (config.type === Number) {
          value = Number(value);
        } else if (config.type === String) {
          value = value == null ? '' : String(value);
        }
        // Array and Object pass through as-is

        if (oldValue === value) return;

        this._properties[name] = value;
        this._changedProperties.set(name, oldValue);

        // Reflect to attribute if configured
        if (config.reflect && this._initialized) {
          this._reflectingProperty = true;
          if (config.type === Boolean) {
            if (value) {
              this.setAttribute(attrName, '');
            } else {
              this.removeAttribute(attrName);
            }
          } else if (value != null) {
            this.setAttribute(attrName, String(value));
          }
          this._reflectingProperty = false;
        }

        if (this._initialized) {
          this.requestUpdate();
        }
      },
      enumerable: true,
      configurable: true,
    });
  }

  // Store attribute mapping on class
  ElementClass._attrToProperty = attrToProperty;
  initializedClasses.add(ElementClass);
}

export class RunElement extends HTMLElement {
  // Expose for test mocking
  static _reducedMotionQuery = reducedMotionQuery;

  static get observedAttributes() {
    // Ensure properties are set up
    setupProperties(this);

    const props = this.properties || {};
    const attrs = [];
    for (const [name, config] of Object.entries(props)) {
      if (config.reflect !== false) {
        attrs.push(toAttributeName(name, config));
      }
    }
    return attrs;
  }

  static get properties() {
    return {};
  }

  // Bound handler for reduced motion changes (per-instance for cleanup)
  #reducedMotionHandler = null;

  // Managed event listeners for cleanup
  #managedListeners = [];

  constructor() {
    super();

    // Ensure property accessors are set up for this class
    setupProperties(this.constructor);

    this._initialized = false;
    this._properties = {};
    this._changedProperties = new Map();
    this._reflectingProperty = false;
    this._updatePending = false;
    this._firstUpdate = true;
    this._updatePromise = null;
    this._updateResolve = null;

    // Initialize property defaults
    const props = this.constructor.properties || {};
    for (const [name, config] of Object.entries(props)) {
      const defaultValue = typeof config.default === 'function'
        ? config.default()
        : config.default;
      this._properties[name] = defaultValue !== undefined ? defaultValue : this._getTypeDefault(config.type);
    }

    // Create bound handler for reduced motion changes
    this.#reducedMotionHandler = (e) => this.#onReducedMotionChange(e);
  }

  _getTypeDefault(type) {
    if (type === Boolean) return false;
    if (type === Number) return 0;
    if (type === String) return '';
    if (type === Array) return [];
    if (type === Object) return {};
    return undefined;
  }

  /**
   * Read-only property reflecting user's reduced motion preference.
   * @returns {boolean} true if user prefers reduced motion
   */
  get reducedMotion() {
    return this.constructor._reducedMotionQuery.matches;
  }

  set reducedMotion(_value) {
    throw new Error('reducedMotion is read-only');
  }

  #onReducedMotionChange(e) {
    this.#syncReducedMotionAttribute(e.matches);
  }

  #syncReducedMotionAttribute(matches) {
    if (matches) {
      this.setAttribute('data-reduced-motion', '');
    } else {
      this.removeAttribute('data-reduced-motion');
    }
  }

  connectedCallback() {
    if (!this._initialized) {
      this._initializeShadowDOM();
      this._initialized = true;

      // Mark all initial properties as changed for first render
      const props = this.constructor.properties || {};
      for (const name of Object.keys(props)) {
        this._changedProperties.set(name, undefined);
      }
    }

    // Set initial reduced motion attribute and listen for changes
    this.#syncReducedMotionAttribute(this.constructor._reducedMotionQuery.matches);
    this.constructor._reducedMotionQuery.addEventListener('change', this.#reducedMotionHandler);

    // Set container query support attribute
    if (cqSupported) {
      this.setAttribute('cq-supported', '');
    }

    // Auto-generate view-transition-name if not set
    if (!this.style.viewTransitionName) {
      this.style.viewTransitionName = `${this.localName}-${++viewTransitionCounter}`;
    }

    // Auto-assign per-instance seed for asset-rendered skin variation
    if (!this.style.getPropertyValue('--_instance-seed')) {
      this.style.setProperty('--_instance-seed', String(Math.random()));
    }

    this.requestUpdate();
  }

  disconnectedCallback() {
    // C+ rendering tier (cplus-rendering-tier-contract-addendum-v1 §2/§3):
    // release GPU resources before the rest of teardown so the shared WebGL
    // context is freed deterministically (net-zero-context invariant).
    if (this._activeCplusSkin) {
      try {
        this._activeCplusSkin.module.deactivate?.(this);
      } catch (err) {
        console.error(`[${this.localName}] error during skin deactivate:`, err);
      }
      this._activeCplusSkin = null;
    }

    // Clean up reduced motion listener
    this.constructor._reducedMotionQuery.removeEventListener('change', this.#reducedMotionHandler);

    // Clean up managed listeners
    for (const { target, type, handler, options } of this.#managedListeners) {
      target.removeEventListener(type, handler, options);
    }
    this.#managedListeners = [];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (this._reflectingProperty) return; // Avoid loops from property->attribute reflection

    const attrMap = this.constructor._attrToProperty;
    if (!attrMap) return;

    const propInfo = attrMap.get(name);
    if (!propInfo) return;

    const { name: propName, config } = propInfo;

    // Convert attribute value to property value
    let value;
    if (config.type === Boolean) {
      value = newValue !== null;
    } else if (config.type === Number) {
      value = newValue === null ? config.default : Number(newValue);
    } else {
      value = newValue === null ? config.default : newValue;
    }

    // Set property (which will trigger update)
    this._properties[propName] = value;
    this._changedProperties.set(propName, oldValue);

    if (this._initialized) {
      this.requestUpdate();
    }
  }

  _initializeShadowDOM() {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }
  }

  /**
   * Request an update. Updates are batched and processed asynchronously.
   */
  requestUpdate() {
    if (this._updatePending) return this.updateComplete;

    this._updatePending = true;
    this._updatePromise = new Promise(resolve => {
      this._updateResolve = resolve;
    });

    // Batch updates via microtask
    queueMicrotask(() => this._performUpdate());

    return this._updatePromise;
  }

  _performUpdate() {
    if (!this._updatePending) return;

    // Updates requested before first connection have no shadow root to render
    // into (it is attached in connectedCallback), so any updated()/firstUpdated()
    // that touches this.shadowRoot would crash. Consume the cycle as a resolved
    // no-op: connectedCallback always queues a fresh update once the shadow root
    // exists, and _changedProperties is left intact so that first real cycle
    // sees everything that changed while detached.
    if (!this._initialized) {
      this._updatePending = false;
      const resolve = this._updateResolve;
      if (resolve) resolve(true);
      return;
    }

    const changedProperties = new Map(this._changedProperties);
    this._changedProperties.clear();
    this._updatePending = false;

    // Capture resolve before lifecycle hooks: hooks may call requestUpdate(),
    // which overwrites this._updateResolve. Using the captured ref ensures THIS
    // update cycle's promise resolves even when a new one is queued mid-lifecycle.
    const resolve = this._updateResolve;

    try {
      // Lifecycle: willUpdate
      this.willUpdate(changedProperties);

      // Render
      this._render();

      // Lifecycle: updated
      this.updated(changedProperties);

      // Lifecycle: firstUpdated (only once)
      if (this._firstUpdate) {
        this._firstUpdate = false;
        this.firstUpdated(changedProperties);
      }
    } catch (err) {
      // Surface lifecycle errors without leaving updateComplete unresolved.
      // An unresolved promise causes silent hangs in test fixtures and any
      // code awaiting the next render.
      console.error(`[${this.localName}] error during update lifecycle:`, err);
    } finally {
      if (resolve) {
        resolve(true);
      }
    }
  }

  _render() {
    if (!this.shadowRoot) return;
    const content = this.render();
    if (typeof content === 'string') {
      // Prepend static styles if defined
      const styles = this.constructor.styles;
      const styleTag = styles ? `<style data-run-styles>${styles}</style>` : '';
      this.shadowRoot.innerHTML = styleTag + content;
    }
  }

  /**
   * Override to return component template
   */
  render() {
    return '';
  }

  /**
   * Called before render when properties change.
   * @param {Map<string, any>} _changedProperties - Map of property names to old values
   */
  willUpdate(_changedProperties) {
    // Override in subclass
  }

  /**
   * Called after render when properties change. Never invoked before the
   * element's first connection — this.shadowRoot is guaranteed to exist here.
   * @param {Map<string, any>} _changedProperties - Map of property names to old values
   */
  updated(_changedProperties) {
    // Override in subclass
  }

  /**
   * Called once after first render.
   * @param {Map<string, any>} _changedProperties - Map of property names to old values
   */
  firstUpdated(_changedProperties) {
    // Override in subclass
  }

  // ==========================================================================
  // C+ RENDERING TIER DISPATCH (cplus-rendering-tier-contract-addendum-v1 §3)
  // ==========================================================================

  /**
   * Apply a resolved skin by its rendering tier. Shared mechanism only — the
   * component still owns skin resolution and passes the resolved module here.
   *
   * `css` tier (or any skin without `meta`) invokes `cssApply` verbatim, so the
   * existing CSS path is behaviourally unchanged. `canvas-2d`/`webgl` tier runs
   * the skin's `render`→`activate` once (the rAF loop owns subsequent visuals)
   * and is an idempotent no-op on later update cycles.
   *
   * @param {object}   opts
   * @param {object}   opts.skinModule  resolved skin module ({styles, render, activate?, deactivate?, meta?})
   * @param {CSSStyleSheet} [opts.styleSheet]  sheet to adopt for the C+ first-apply
   * @param {() => void} [opts.cssApply]  the component's original CSS apply lines
   */
  _applySkinTier({ skinModule, styleSheet, cssApply }) {
    const tier = skinModule?.meta?.renderingTier ?? 'css';

    if (tier === 'css') {
      // Switching back to a CSS skin from an active C+ skin: release it first.
      if (this._activeCplusSkin) {
        try {
          this._activeCplusSkin.module.deactivate?.(this);
        } catch (err) {
          console.error(`[${this.localName}] error during skin deactivate:`, err);
        }
        this._activeCplusSkin = null;
      }
      cssApply?.();
      return;
    }

    // canvas-2d / webgl. Idempotent: already active for this module → no-op
    // (the shared renderer's rAF loop owns all subsequent visual updates).
    if (this._activeCplusSkin?.module === skinModule) return;

    if (styleSheet) this.shadowRoot.adoptedStyleSheets = [styleSheet];
    skinModule.render(this);
    const canvas = this.shadowRoot.querySelector('canvas');
    if (!canvas) {
      throw new Error(
        `[${this.localName}] ${tier} skin render() produced no <canvas>`
      );
    }
    skinModule.activate?.(this, canvas);
    this._activeCplusSkin = { module: skinModule };

    // tokenDidChange wiring (addendum §8): the observer is attached ONLY when a
    // skin declares non-empty meta.tokens AND exports tokenDidChange. No skin in
    // RUN-380 does (smoke skin declares tokens: []), so this path is provably
    // never taken here. The concrete style-observer mechanism is the first
    // token-consuming skin's concern (RUN-298) per the addendum's
    // substrate-vs-first-consumer scope split (§7) — the contract surface and
    // the absent/empty-tokens path ship here; the observer impl does not.
    const tokens = skinModule.meta?.tokens ?? [];
    if (tokens.length && typeof skinModule.tokenDidChange === 'function') {
      throw new Error(
        `[${this.localName}] token-consuming C+ skins require the style-observer ` +
        `mechanism (RUN-298, addendum §8) — not implemented by the RUN-380 substrate`
      );
    }
  }

  /**
   * Promise that resolves after the next render completes.
   */
  get updateComplete() {
    return this._updatePromise || Promise.resolve(true);
  }

  /**
   * Alias for requestUpdate(). Engine-built components call requestRender();
   * this ensures compatibility without patching in showcase HTML files.
   */
  requestRender() {
    return this.requestUpdate();
  }

  // ==========================================================================
  // INSPECTOR / X-RAY
  // ==========================================================================

  /**
   * Build a property manifest from the component's declared properties.
   * Supports two patterns:
   *   1. Declarative: static get properties() { return { name: { type, default, reflect } } }
   *   2. Manual: static get observedAttributes() + public getters (rebuilt components)
   * @returns {Array<{name: string, type: string, value: any, reflect: boolean}>}
   */
  _buildPropertyManifest() {
    const props = this.constructor.properties || {};
    const entries = Object.entries(props);

    // Pattern 1: declarative property system
    if (entries.length > 0) {
      return entries.map(([name, config]) => ({
        name,
        type: config.type?.name ?? 'String',
        default: config.default,
        value: this[name],
        reflect: config.reflect !== false,
      }));
    }

    // Pattern 2: manual observedAttributes + public getters
    const attrs = this.constructor.observedAttributes;
    if (!attrs || attrs.length === 0) return [];

    return attrs.map(attr => {
      // Convert kebab-case attribute to camelCase property name
      const prop = attr.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      const value = this[prop];
      const type = value === undefined ? 'String'
        : typeof value === 'boolean' ? 'Boolean'
        : typeof value === 'number' ? 'Number'
        : 'String';
      return { name: prop, type, value, reflect: true };
    });
  }

  /**
   * Return a full inspection manifest for this component instance.
   * Consumed by X-Ray mode and the showcase playground.
   * @returns {object}
   */
  getInspectorManifest() {
    return {
      tagName: this.localName,
      componentName: this.constructor.componentName ?? this.localName,
      version: this.constructor.version ?? '0.0.0',
      category: this.constructor.category ?? 'unknown',
      layer: this.constructor.layer ?? 'atomic',
      properties: this._buildPropertyManifest(),
      parts: this.constructor.parts ?? [],
      slots: this.constructor.slots ?? [],
      skin: this._resolvedSkinName ?? 'default',
      events: this.constructor.events ?? [],
    };
  }

  // ==========================================================================
  // UTILITY METHODS
  // ==========================================================================

  /**
   * Escape HTML entities to prevent XSS.
   * @param {any} str - String to escape (non-strings are converted)
   * @returns {string} Escaped string safe for innerHTML
   */
  safeHTML(str) {
    const text = String(str);
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ==========================================================================
  // EVENT HELPERS
  // ==========================================================================

  /**
   * Emit a custom event from this element.
   * @param {string} name - Event name
   * @param {any} detail - Event detail payload
   * @param {CustomEventInit} options - Additional event options
   * @returns {boolean} - Whether the event was not cancelled
   */
  emit(name, detail, options = {}) {
    const event = new CustomEvent(name, {
      bubbles: true,
      composed: true,
      cancelable: false,
      ...options,
      detail,
    });
    return this.dispatchEvent(event);
  }

  /**
   * Add an event listener that will be automatically cleaned up on disconnect.
   * @param {EventTarget} target - Target to listen on
   * @param {string} type - Event type
   * @param {EventListener} handler - Event handler
   * @param {AddEventListenerOptions} options - Listener options
   */
  listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.#managedListeners.push({ target, type, handler, options });
  }
}

// Apply to base class (subclasses call this in their definition)
export function defineElement(ElementClass, tagName) {
  setupProperties(ElementClass);
  if (!customElements.get(tagName)) {
    customElements.define(tagName, ElementClass);
  }
  return ElementClass;
}

export default RunElement;
